/** Static copy for the palmistry result, ported from lpr-web `palmReading:v4.*`. */
export const palmCopy = {
  title: "Palm Insights",
  /** `scanDate` is the formatted report date (e.g. "Feb 24, 2025"); "" hides it. */
  subtitle: (scanDate: string) => (scanDate ? `From your hand scan on ${scanDate}` : "From your hand scan"),
  heartOfNature: {
    title: "What Your Hand Reveal",
    subtitle:
      "Your palm is more than skin and lines—it’s a quiet blueprint of your nature, strengths, and hidden patterns. From how you lead and love to how you handle pressure, this reading uncovers what your hands have been holding all along.",
  },
  conclusion: { title: "Conclusion" },
  scanBasedReading: {
    title: "Scan-Based Reading",
    /** `scanDate` is the formatted report date (e.g. "February 24, 2025"); "" hides it. */
    description: (scanDate: string) =>
      scanDate
        ? `This palm reading summary is based on the detailed scan of your hand taken on ${scanDate}, and reflects the insights drawn from that specific moment.`
        : "This palm reading summary is based on the detailed scan of your hand and reflects the insights drawn from that specific moment.",
  },
  continueJourney: {
    title: "Continue Your Journey",
    description:
      "These gifted advisors can help you go beyond the lines—offering deeper guidance based on your unique palm reading.",
  },
  writtenTheLines: {
    title: "Written in the Lines",
    button: "Ask advisor",
    /** `{section}` is replaced with the line's name. */
    unlockExpert: "Unlock your {section} secrets with expert advisors",
  },
} as const

export type ChartType = "health" | "success" | "romance" | "career"
export type ChartLevel = "low" | "medium" | "high"

/** Per-chart copy by level (label + description), used by the line-section meters. */
export const chartMeterCopy: Record<
  ChartType,
  { title: string } & Record<ChartLevel, { label: string; description: string }>
> = {
  health: {
    title: "Health",
    low: {
      label: "Energy deficit",
      description:
        "Your body might be lacking the energy it needs. Rest, hydration, and relaxation can help.",
    },
    medium: {
      label: "In recovery",
      description:
        "You're on the mend! A bit more time and care will bring you back to full energy.",
    },
    high: {
      label: "Flourishing",
      description:
        "Your body is thriving! Keep nurturing yourself for continued strength and vitality",
    },
  },
  success: {
    title: "Success",
    low: {
      label: "Needs focus",
      description:
        "Your potential is there, but lacks direction. Refocusing can reignite your path.",
    },
    medium: {
      label: "On track",
      description: "You’re moving with purpose. Stay the course, and results will follow.",
    },
    high: {
      label: "Thriving",
      description: "You’re growing with confidence. Your dedication is showing real rewards.",
    },
  },
  romance: {
    title: "Romance",
    low: {
      label: "Guarded",
      description:
        "You keep your heart close, protecting it from hurt. Trust may take time, and that’s okay.",
    },
    medium: {
      label: "Warming Up",
      description: "Your heart is opening. Love feels close, but you still need time and trust.",
    },
    high: {
      label: "Devoted",
      description:
        "You pour your soul into love. Emotion runs deep, and connection is sacred to you.",
    },
  },
  career: {
    title: "Career",
    low: {
      label: "Dormant",
      description:
        "You're at the start of something new. Keep moving forward — the journey matters.",
    },
    medium: {
      label: "On the way",
      description: "Momentum is on your side. Trust the process and keep pushing forward.",
    },
    high: {
      label: "Established",
      description:
        "You’re operating at full potential. This is a moment of clarity and fulfillment.",
    },
  },
}

/** Out-of-credits paywall is shared with advisors; chat reuses the credits modal. */
export const scanCopy = {
  intro: {
    title: "Create Your Palm Report",
    subtitle:
      "Complete a short hand-scan quiz so we can prepare your personalized palmistry reading",
    notNow: "Not now",
    start: "Start scan",
  },
  instruction: {
    title: "Take a photo\nof your left palm",
    takePhoto: "Take a photo",
    upload: "Upload from gallery",
  },
  camera: {
    title: (hand: "left" | "right" = "left") => `Let's capture your ${hand} palm`,
    subtitle: "Keep your hand open, centered, and in good light",
    capture: "Capture",
    starting: "Starting camera…",
  },
  result: {
    retake: "Retake",
    continue: "Continue",
    noHand: "We couldn't detect a palm. Please try a clearer photo.",
  },
  prepared: {
    title: "Your Palm Report\nIs Being Prepared",
    subtitle:
      "Thank you. We've received your palm photo. Our specialists are analyzing your lines, marks, and hand patterns to prepare your personal reading.",
    unlocksIn: "Your report unlocks in",
    button: "Got it",
  },
} as const
